import React, { createContext, useContext, useState, useEffect } from 'react';
import { BucketImage } from '../hooks/useBucketImage';

export interface Product {
	id: string;
	name: string;
	price: number;
	type?: string;
	header?: BucketImage | string;
	grants?: any;
	subscription?: string;
}

interface BasketContextType {
	basket: Product[];
	loadBasketCallback: () => void;
	setBasket: React.Dispatch<React.SetStateAction<Product[]>>;
	addToBasket: (product: Product) => void;
	removeFromBasket: (productId: string) => void;
	clearBasket: () => void;
	setAlreadySelected: (selected: boolean) => void;
	alreadySelected: boolean;
	basketEl: SVGSVGElement | null;
	setBasketEl: React.Dispatch<React.SetStateAction<null | SVGSVGElement>>;
}

const BasketContext = createContext<BasketContextType | undefined>(undefined);

export const BasketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [basketEl, setBasketEl] = React.useState<null | SVGSVGElement>(null);
	const [basket, setBasket] = useState<Product[]>([]);
	const [_alreadySelected, _setAlreadySelected] = useState(false);

	// Function to get the appropriate basket key (either personal or organization)
	const getBasketKey = () => {
		const basketIdentifier = sessionStorage.getItem('Inspirus_catalog_identifier') || 'personal';
		return `Inspirus_${basketIdentifier}_basket`;
	};

	// Function to load the correct basket from localStorage
	const loadBasket = () => {
		const basketKey = getBasketKey();
		const storedBasket = localStorage.getItem(basketKey);
		if (storedBasket) {
			const parsedBasket = JSON.parse(storedBasket);
			setBasket(parsedBasket.basket);
			_setAlreadySelected(parsedBasket.alreadySelected);
		} else {
			setBasket([]);
			_setAlreadySelected(false);
		}
	};

	// Load the correct basket when the component mounts or when the basket identifier changes
	useEffect(() => {
		loadBasket();
	}, [sessionStorage.getItem('Inspirus_catalog_identifier')]);

	// Save the basket and state to localStorage based on the selected basket type
	useEffect(() => {
		const basketKey = getBasketKey();
		if (basket.length > 0 || _alreadySelected) {
			localStorage.setItem(
				basketKey,
				JSON.stringify({
					basket,
					alreadySelected: _alreadySelected
				})
			);
		} else {
			localStorage.removeItem(basketKey);
		}
	}, [basket, _alreadySelected]);

	// Add product to the current basket
	const addToBasket = (product: Product) => {
		// Ensure the correct basket is loaded before adding a product
		loadBasket();
		setBasket((prevBasket) => {
			const basketKey = getBasketKey();
			const updatedBasket = [...prevBasket, product];
			localStorage.setItem(
				basketKey,
				JSON.stringify({ basket: updatedBasket, alreadySelected: _alreadySelected })
			);
			return updatedBasket;
		});
	};

	// Remove product from the current basket
	const removeFromBasket = (productId: string) => {
		// Ensure the correct basket is loaded before removing a product
		loadBasket();
		setBasket((prevBasket) => {
			const basketKey = getBasketKey();
			const updatedBasket = prevBasket.filter((product) => product.id !== productId);
			localStorage.setItem(
				basketKey,
				JSON.stringify({ basket: updatedBasket, alreadySelected: _alreadySelected })
			);
			return updatedBasket;
		});
	};

	// Clear the current basket
	const clearBasket = () => {
		const basketKey = getBasketKey();
		setBasket([]);
		_setAlreadySelected(false);
		localStorage.removeItem(basketKey);
	};

	const setAlreadySelected = (selected: boolean) => {
		_setAlreadySelected(selected);
	};

	const alreadySelected = _alreadySelected;

	return (
		<BasketContext.Provider
			value={{
				basket,
				loadBasketCallback: loadBasket,
				setBasket,
				addToBasket,
				removeFromBasket,
				clearBasket,
				setAlreadySelected,
				alreadySelected,
				basketEl,
				setBasketEl
			}}
		>
			{children}
		</BasketContext.Provider>
	);
};

export const useBasket = () => {
	const context = useContext(BasketContext);
	if (!context) {
		throw new Error('useBasket must be used within a BasketProvider');
	}
	return context;
};
